<template>
  <div class="c-partner-score-tag">
    <component
      :is="wrapperComponent"
      placement="bottomLeft"
      :mount-to-body="true"
      overlay-class="c-partner-score-tag__popover-overlay"
      :trigger="Trigger.Hover"
    >
      <BittsTag
        class="c-partner-score-tag__tag"
        :class="partnerScoreClass"
        :color="partnerScoreColor"
        size="x-small"
      >
        <div class="flex items-center">
          <FontAwesomeIcon
            v-if="hasPartnerScore"
            :icon="['fak', 'partner-score']"
            class="text-12 mr-2"
          />
          <span v-else class="block h-12" />
          <div class="text-12 font-bold">
            {{ scoreCopy }}
          </div>
        </div>
      </BittsTag>
      <template #content v-if="showBreakdown">
        <PartnerScoreBreakdown :partner="partner" />
      </template>
      <template #title v-if="showUnknown">
        Missing information to calculate partner score
      </template>
      <template #title v-else-if="showNone">
        Score requires at least 10 closed-won deals with this partner's accounts
        in the past 365 days
      </template>
    </component>
  </div>
</template>

<script setup lang="ts">
import { BittsPopover, BittsTag, BittsTooltip } from '@crossbeam/bitts';
import { Trigger } from '@crossbeam/bitts/src/types';

import { capitalize } from 'lodash';
import { computed } from 'vue';

import { PARTNER_SCORES, PARTNER_SCORE_COLORS } from '@/constants/partners';
import type { Partner, PartnerTrimmed } from '@/types/partners';

import PartnerScoreBreakdown from './PartnerScoreBreakdown.vue';

const { partner, withBreakdown = false } = defineProps<{
  partner: Partner | PartnerTrimmed;
  withBreakdown?: boolean;
}>();

const partnerScoreBucket = computed(
  () => partner.metrics?.partner_score_bucket ?? PARTNER_SCORES.NONE,
);
const hasPartnerScore = computed(
  () =>
    partnerScoreBucket.value !== PARTNER_SCORES.NONE &&
    partnerScoreBucket.value !== PARTNER_SCORES.UNKNOWN,
);
const showUnknown = computed(
  () => partnerScoreBucket.value === PARTNER_SCORES.UNKNOWN,
);
const showNone = computed(
  () => partnerScoreBucket.value === PARTNER_SCORES.NONE,
);
const showBreakdown = computed(() => withBreakdown && hasPartnerScore.value);
const wrapperComponent = computed(() => {
  if (showBreakdown.value) return BittsPopover;
  if (showUnknown.value || showNone.value) return BittsTooltip;
  return 'div';
});

const scoreCopy = computed(() => capitalize(partnerScoreBucket.value));

const partnerScoreColor = computed(
  () => PARTNER_SCORE_COLORS[partnerScoreBucket.value],
);
const partnerScoreClass = computed(
  () => `c-partner-score-tag__tag--${partnerScoreBucket.value}`,
);
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.c-partner-score-tag__popover-overlay {
  &.bitts-overflow-menu.ant-popover .ant-popover-inner {
    @apply max-w-[360px] p-12;
  }
}
</style>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-partner-score-tag__tag {
  :deep(.ant-tag) {
    @apply inset-ring pl-4 pr-6 rounded-6;
  }
}
.c-partner-score-tag__tag--high {
  :deep(.ant-tag) {
    @apply inset-ring-success-border;
  }
}
.c-partner-score-tag__tag--medium {
  :deep(.ant-tag) {
    @apply inset-ring-warning-border;
  }
}
.c-partner-score-tag__tag--low {
  :deep(.ant-tag) {
    @apply inset-ring-accent-border;
  }
}
.c-partner-score-tag__tag--none,
.c-partner-score-tag__tag--unknown {
  :deep(.ant-tag) {
    @apply inset-ring-neutral-border px-4;
  }
}
</style>
